import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function KayakingAdventures() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    { question: 'Do I need kayaking experience?', answer: 'No! Flatwater kayaking tours are perfect for complete beginners. Guides provide all necessary instruction on paddling technique and safety. Stable sit-on-top kayaks are very easy to control. For whitewater kayaking, tandem options allow beginners to experience rapids safely with an expert guide, while solo whitewater requires either experience or enrollment in a multi-day training course.' },
    { question: 'Is kayaking safe? What about hippos and crocodiles?', answer: 'Yes, kayaking is very safe with licensed operators. The sections used for kayaking are generally safe from dangerous animals. While hippos and crocodiles exist in the Nile, they are rarely encountered in the main paddling areas and guides know how to avoid them. All participants wear life jackets, receive safety briefings, and are accompanied by experienced guides. The deep water minimizes risks even in rapids.' },
    { question: 'What is the minimum age for kayaking?', answer: 'For flatwater kayaking, there is typically no strict minimum age—children as young as 4-8 can participate in family-friendly tours with parental supervision. For tandem whitewater kayaking on Grade 5 rapids, the minimum age is 14 years. Solo whitewater kayaking is generally for ages 16+ or by instructor assessment.' },
    { question: 'When is the best time to go kayaking?', answer: 'Kayaking is available year-round thanks to consistent water levels regulated by Lake Victoria. The dry seasons (December-February and June-September) offer the most favorable weather with clear skies and strong sun. However, the wet seasons provide lush green landscapes perfect for photography. Water temperature remains a comfortable 23°C year-round.' },
    { question: 'How long are kayaking trips?', answer: 'Flatwater tours typically last 2-3 hours. Tandem whitewater kayaking is available as half-day (3-4 hours) or full-day (6-8 hours) trips. Solo whitewater experiences range from full-day sessions to multi-day courses (3-5 days) for comprehensive skill building. Sunset paddles are usually 1.5-2 hours.' },
    { question: 'What\'s included in the kayaking experience?', answer: 'All kayaking trips include the kayak, paddle, life jacket, helmet (for whitewater), and expert guide. Most tours include transfers from Jinja town, safety briefings, and drinking water. Full-day trips typically include lunch. Some operators offer professional photography services for an additional fee.' },
    { question: 'Can I combine kayaking with other activities?', answer: 'Absolutely! Jinja offers numerous adventure activities. Many visitors combine kayaking with whitewater rafting, bungee jumping, stand-up paddleboarding, river tubing, or quad biking. Multi-day adventure packages can be customized to include several activities. Contact us to create your perfect Jinja adventure itinerary!' },
    { question: 'What if I can\'t swim?', answer: 'Basic swimming ability is recommended but not strictly required for flatwater kayaking, as life jackets are mandatory and keep you afloat. However, you should be comfortable in water. For whitewater kayaking, swimming ability is more important. Always inform your guide of your swimming level so they can provide appropriate guidance and positioning.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1484876065684-b683cf17d276?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Kayaking Adventures on the Nile
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Paddle the world's longest river at its spectacular source in Jinja
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(350+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                Safety Certified
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-life-ring" className="w-4 h-4 mr-2" />
                All Equipment Provided
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Beginner Friendly
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Kayak Adventure
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Paddle the Legendary Nile at Its Source
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Kayaking on the Nile River at its source near Jinja offers paddlers a unique perspective on one of Earth's most legendary waterways. The experience combines serene natural beauty with wildlife encounters as African fish eagles circle overhead, monitor lizards sun themselves on rocky outcrops, and hippos observe from a safe distance. Paddlers glide across crystal-clear water, exploring the river where it begins its 6,650-kilometer journey to the Mediterranean Sea.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Based in Jinja—Uganda's adventure capital—Nile kayaking offers experiences for every skill level and time frame. From gentle two-hour flatwater paddles perfect for beginners and families, to challenging full-day whitewater expeditions through Grade 3-5 rapids, to multi-day wilderness adventures, the Nile provides endless opportunities to explore by paddle. No prior kayaking experience is required for introductory trips, and all necessary equipment including kayaks, paddles, life jackets, and helmets is provided.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The upper Nile near Jinja offers incredibly diverse paddling environments. Calm channels around the Source of the Nile and Bujagali area provide perfect conditions for wildlife viewing and photography, with stable sit-on-top kayaks ideal for first-timers. Intermediate sections feature Class II-III rapids that build confidence and skills. For experienced paddlers seeking adrenaline, the same world-class Grade 5 rapids that make Jinja famous for rafting can be tackled in specialized whitewater kayaks with expert instruction.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Types of Kayaking Experiences</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong>Flatwater Kayaking (Kalagala Tours):</strong> The most popular option for beginners and those seeking a peaceful experience. These two-hour guided tours use stable sit-on-top kayaks and explore calm sections around the Source of the Nile or Bujagali. Paddlers can sit or stand while observing kingfishers, herons, African fish eagles, and monitor lizards. No experience needed—guides provide all instruction and highlight local wildlife and history.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong>Source of the Nile Tours:</strong> A bucket-list experience! Guided tours take you to the official source where the Nile begins, offering views of Lake Victoria and the historic marker. Some tours allow you to paddle out into Lake Victoria before drifting back into the Nile current. This is perfect for photography enthusiasts and history buffs wanting to paddle where legendary explorers once searched.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong>Tandem Whitewater Kayaking:</strong> For those wanting whitewater thrills without extensive training, tandem kayaks offer the solution. You sit in front while an experienced guide steers and instructs from behind, navigating Grade 3-5 rapids together. Half-day and full-day options available. Minimum age 14 years.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  <strong>Solo Whitewater Kayaking:</strong> For experienced kayakers or those willing to invest in multi-day courses, solo whitewater kayaking on the Nile's Grade 3-5 rapids provides the ultimate challenge. Qualified coaches teach paddling strokes, rescue tactics, and river reading skills. Multi-day courses build from basics to advanced techniques.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Wildlife & Natural Beauty</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The calm sections of the Nile near Jinja are safe from dangerous animals like hippos and crocodiles in the main paddling areas, though they can occasionally be spotted from a distance. The river ecosystem teems with birdlife—kingfishers dive for fish, herons stalk the shallows, and majestic African fish eagles soar overhead. Monitor lizards bask on rocky outcrops, and vervet monkeys watch from riverside trees. The lush tropical islands, rocky channels, and papyrus-lined banks create stunning photographic opportunities, especially during sunrise and sunset paddles.
                </p>

                <h3 className="text-2xl font-bold text-safari-green mt-8 mb-4">Safety & Professional Standards</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  All kayaking operators in Jinja are licensed and adhere to stringent safety protocols. Experienced, internationally certified guides lead all trips, providing detailed safety briefings and paddling instruction before launching. Life jackets and helmets are mandatory for all participants. Safety kayakers and rescue teams accompany whitewater trips. The Nile's deep waters minimize the risk of hitting rocks even in rapids. Choose reputable operators with proven safety records, and always follow guide instructions.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Kayak the Nile in Jinja
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-water', title: 'Paddle the Source of the Nile', description: 'Explore the legendary birthplace of the world\'s longest river—a true bucket-list experience' },
                  { icon: 'fa-users', title: 'All Skill Levels Welcome', description: 'From gentle flatwater tours for beginners to Grade 5 whitewater for experts' },
                  { icon: 'fa-hippo', title: 'Abundant Wildlife Viewing', description: 'Spot African fish eagles, kingfishers, herons, monitor lizards, and occasional hippos' },
                  { icon: 'fa-sun', title: 'Year-Round Paddling', description: 'Consistent water levels and warm 23°C temperatures make kayaking perfect any time' },
                  { icon: 'fa-tent', title: 'Multi-Day Adventures', description: 'Extended courses and wilderness camping trips for immersive Nile experiences' },
                  { icon: 'fa-shield-halved', title: 'Expert Instruction', description: 'Internationally certified guides provide safety briefings and technique training' },
                  { icon: 'fa-camera', title: 'Stunning Photography', description: 'Capture sunrise/sunset paddles, tropical islands, and dramatic rapids' },
                  { icon: 'fa-clock', title: 'Flexible Durations', description: 'Choose from 2-hour tours to full-day expeditions to multi-day courses' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Kayaking Options */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Kayaking Experience Options
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    title: 'Flatwater Kayaking',
                    duration: '2 hours',
                    difficulty: 'Beginner',
                    description: 'Gentle paddle on calm sections using stable sit-on-top kayaks. Perfect for families, first-timers, and wildlife viewing.',
                    includes: ['Stable kayak & paddle', 'Life jacket', 'Expert guide', 'Wildlife spotting', 'No experience needed']
                  },
                  {
                    title: 'Source of the Nile Tour',
                    duration: '2-3 hours',
                    difficulty: 'Beginner',
                    description: 'Paddle to the historic source where the Nile begins. Explore Lake Victoria\'s edge and the official source marker.',
                    includes: ['Guided tour', 'Historical insights', 'Photo opportunities', 'Lake Victoria access', 'All equipment']
                  },
                  {
                    title: 'Tandem Whitewater Kayaking',
                    duration: 'Half or Full Day',
                    difficulty: 'Moderate',
                    description: 'Navigate Grade 3-5 rapids in a double kayak with an expert guide steering from behind. Thrilling yet safe.',
                    includes: ['Tandem kayak', 'Expert guide in kayak', 'Safety gear', 'Lunch (full day)', 'Min age 14 years']
                  },
                  {
                    title: 'Solo Whitewater Kayaking',
                    duration: 'Full Day or Multi-Day',
                    difficulty: 'Advanced',
                    description: 'Tackle Grade 3-5 rapids solo with professional coaching. Multi-day courses available for skill building.',
                    includes: ['Whitewater kayak', 'Certified coach', 'Safety team', 'Technique training', 'Rescue instruction']
                  }
                ].map((option, index) => (
                  <div key={index} className="card p-6">
                    <h3 className="text-2xl font-bold text-safari-green mb-3">{option.title}</h3>
                    <div className="flex gap-4 mb-4 text-sm">
                      <span className="bg-safari-light text-safari-green px-3 py-1 rounded-full font-semibold">
                        <FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-1" />
                        {option.duration}
                      </span>
                      <span className="bg-safari-light text-safari-green px-3 py-1 rounded-full font-semibold">
                        <FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-1" />
                        {option.difficulty}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-4">{option.description}</p>
                    <ul className="space-y-2">
                      {option.includes.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Sample Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Flatwater Kayaking Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be fully customized based on your preferences,
                  skill level, and pickup location. We can arrange sunrise paddles, sunset tours, or combine kayaking with other Jinja activities.
                  Just let us know what works best for you!
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '08:00', title: 'Pickup & Transfer', desc: 'Pickup from your Jinja accommodation and transfer to the kayaking launch site at Bujagali or Source of the Nile.' },
                  { time: '08:30', title: 'Safety Briefing & Equipment', desc: 'Comprehensive safety briefing covering paddling technique, water safety, and wildlife viewing etiquette. Get fitted with life jacket and receive your kayak and paddle.' },
                  { time: '09:00', title: 'Launch & Paddle Practice', desc: 'Launch into calm water and practice basic strokes with your guide. Learn forward paddling, turning, and stopping techniques.' },
                  { time: '09:30', title: 'Wildlife Exploration', desc: 'Paddle through scenic channels observing kingfishers, herons, African fish eagles, and monitor lizards. Your guide shares insights about the river ecosystem and local history.' },
                  { time: '10:30', title: 'Photo Opportunities', desc: 'Stop at scenic spots for photography. Paddle to tropical islands and rocky outcrops for unique perspectives of the Nile.' },
                  { time: '11:00', title: 'Return & Transfer', desc: 'Paddle back to the launch site. Enjoy refreshments before transfer back to your accommodation.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* What to Bring */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring for Kayaking
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-person-swimming" className="w-6 h-6" />
                    Essential Clothing
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Swimsuit or quick-dry shorts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Quick-dry shirt or rash guard (sun protection)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Water shoes or sports sandals with straps</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Hat or cap with secure strap</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Change of dry clothes for after</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-sun" className="w-6 h-6" />
                    Sun Protection & Extras
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof sunscreen (SPF 50+)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sunglasses with secure strap</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Waterproof bag for valuables</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Towel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Water bottle (reusable)</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 p-6 bg-white rounded-lg border-l-4 border-safari-gold shadow-sm">
                <p className="text-gray-700">
                  <strong className="text-safari-green">Camera Tip:</strong> Waterproof cameras or GoPros with secure mounts are ideal for kayaking. Regular phones and cameras should be left in waterproof bags. Many operators offer professional photography services—ask when booking!
                </p>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Frequently Asked Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Jinja Water Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/whitewater-rafting" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800"
                      alt="Whitewater Rafting"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Whitewater Rafting the Nile</h3>
                    <p className="text-gray-700 mb-4">Conquer legendary Grade 5 rapids on the world's most famous river!</p>
                    <p className="text-safari-gold font-bold">Activity fees apply – contact us for pricing</p>
                  </div>
                </Link>

                <Link to="/activities/stand-up-paddleboarding" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1502933691298-84fc14542831?w=800"
                      alt="Stand-Up Paddleboarding"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Stand-Up Paddleboarding</h3>
                    <p className="text-gray-700 mb-4">Glide peacefully across the Nile's calm waters with stunning views.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply – contact us for pricing</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 2 hours to multi-day</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Beginner to Advanced</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group Size:</strong> Small groups or private</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> Varies by experience type</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Kayaking Adventure
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Kayak and paddle",
                    "Life jacket (mandatory)",
                    "Helmet (whitewater trips)",
                    "Experienced guide",
                    "Safety briefing and instruction",
                    "Transfers from Jinja town",
                    "Drinking water",
                    "Lunch (full-day trips)",
                    "All safety equipment"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Professional photos (available for purchase)",
                    "Transfers from Kampala (can be arranged)",
                    "Accommodation before/after",
                    "Tips for guides",
                    "Personal expenses",
                    "Travel insurance (recommended)",
                    "Waterproof camera/GoPro"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Swimming:</strong> Basic ability recommended, not strictly required for flatwater.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Low to moderate. Flatwater is easy; whitewater requires more stamina.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> Not suitable for pregnant women or those with serious heart conditions.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Comfort:</strong> Must be comfortable being on water.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Kayaking Adventures on the Nile - Jinja"
      />
    </div>
  )
}
